/** @jsx jsx */
import { jsx } from '@emotion/core'
import { Fragment, useState } from 'react'
import { observer } from 'mobx-react'
import { ChangeNum, CenterWrap, OddsChangeAnimationStopTime, UpDownIcoStyle } from '@styles/match.style'
import { Radio } from 'antd'
import { Down, getSpecialBetValue, Up } from '@views/Matchup/MatchField/PreMatchPage/MatchBlock/OddsBlock/OddsButtonGroup'
import { BLOCK_TYPE } from '@views/Matchup/MatchField/PreMatchPage/MatchBlock/OddsBlock'
import { MarketTrack, MatchTrack } from '@store/matchupStore'
import { Schema$MarketBlock, Schema$Outcome } from '@protocol/game'
import _ from 'lodash'
import { ENABLE_MIN_BET_RATE, makeSelectionValue } from '@game/util'
import { convertRate, isLsportsEnv } from '@utils/utils'
import useRootStore from '@store/useRootStore'
import { DownIco, UpIco } from '@shared/Icon/ArrowIcon'
import { ProducerType } from '@game-type/betradar-type'

interface OddsButtonGroupProps {
  odds: MarketTrack
  market: Schema$MarketBlock
  match: MatchTrack
  homeKey: string
  awayKey: string
  oddsBlockType: string
  onClickField: (e) => void
  disabled: boolean
  field: string
  enabledHomeFromViewController: boolean
  enabledAwayFromViewController: boolean
  tabIndex: number
  homeOutcomeMetaId: string
  awayOutcomeMetaId: string
  minimumLiveMatchOutcomeRate: number
}

export const PointUp = (
  <UpDownIcoStyle className="up">
    <UpIco/>
  </UpDownIcoStyle>
)

export const PointDown = (
  <UpDownIcoStyle className="down">
    <DownIco/>
  </UpDownIcoStyle>
)

function OddsButtonGroup(props: OddsButtonGroupProps) {
  const {
    odds,
    market,
    match,
    homeKey, // lsports 는 아웃컴 고유 아이디, 벳레이더는 아웃컴메타 아이디
    awayKey,
    oddsBlockType,
    onClickField,
    disabled,
    field,
    enabledHomeFromViewController,
    enabledAwayFromViewController,
    tabIndex,
    homeOutcomeMetaId, // 오로지 lsports 를 위해서.
    awayOutcomeMetaId, // 오로지 lsports 를 위해서.
    minimumLiveMatchOutcomeRate,
  } = props

  const emptyOdds: Schema$Outcome = { type: null, rate: 1, oldRate: 1, metaId: '1' }

  let homeOutcome = _.find(market.outcomes, { type: homeKey }) || emptyOdds
  let awayOutcome = _.find(market.outcomes, { type: awayKey }) || emptyOdds

  if (isLsportsEnv()) {
    homeOutcome = _.find(market.outcomes, { metaId: homeOutcomeMetaId }) || emptyOdds
    awayOutcome = _.find(market.outcomes, { metaId: awayOutcomeMetaId }) || emptyOdds
  }

  const homeRate = homeOutcome.rate
  const awayRate = awayOutcome.rate

  const [oldHomeRate, setOldHomeRate] = useState(homeRate)
  const [oldAwayRate, setOldAwayRate] = useState(awayRate)

  const [homeRateChangeClass, setHomeRateChangeClass] = useState('')
  const [awayRateChangeClass, setAwayRateChangeClass] = useState('')

  const [homeRateDiff, setHomeRateDiff] = useState(0)
  const [awayChangedRate, setAwayChangedRate] = useState(0)

  const [homeRateChangeAnimationTimeoutId, setHomeRateChangeAnimationTimeoutId] = useState(null)
  const [awayRateChangeAnimationTimeoutId, setAwayRateChangeAnimationTimeoutId] = useState(null)

  const { matchupStore, authStore } = useRootStore()
  const { cart } = matchupStore
  const { matchId } = match

  const makeOddsButtonGroupViewKey = () => {
    const { id } = match
    return `OddsButtonGroup:Row:${id}:${matchId}:${oddsBlockType}`
  }

  const getRateDiff = (oldRate, currentRate) => {
    return Number((oldRate !== 0 ? currentRate - oldRate : 0).toFixed(2))
  }

  const processRateDiff = () => {
    const homeDiff = getRateDiff(oldHomeRate, homeRate)
    if (homeDiff !== 0) {
      clearTimeout(homeRateChangeAnimationTimeoutId)

      const timeoutId = setTimeout(() => {
        setHomeRateChangeClass('')
        setHomeRateDiff(0)
      }, OddsChangeAnimationStopTime)

      setHomeRateChangeAnimationTimeoutId(timeoutId)
      setHomeRateChangeClass(homeDiff > 0 ? 'up' : 'down')
      setOldHomeRate(homeOutcome.rate)
      setHomeRateDiff(homeDiff)
    }

    const awayDiff = getRateDiff(oldAwayRate, awayRate)
    if (awayDiff !== 0) {
      clearTimeout(awayRateChangeAnimationTimeoutId)

      const timeoutId = setTimeout(() => {
        setAwayRateChangeClass('')
        setAwayChangedRate(0)
      }, OddsChangeAnimationStopTime)

      setAwayRateChangeAnimationTimeoutId(timeoutId)
      setAwayRateChangeClass(awayDiff > 0 ? 'up' : 'down')
      setOldAwayRate(awayOutcome.rate)
      setAwayChangedRate(awayDiff)
    }
  }

  const isSelectionDisabled = (enabledFromViewController: boolean, rate: number): boolean => {
    let isDisabled = false

    const findCart = _.find(cart, { matchId })
    if (findCart) {
      isDisabled = !enabledFromViewController
    }

    if (rate < ENABLE_MIN_BET_RATE ||
      (matchupStore.producerId === ProducerType.LIVEODDS && rate < minimumLiveMatchOutcomeRate)) {
      isDisabled = true
    }

    return isDisabled
  }

  processRateDiff()

  const oddsButtonGroupViewKey = makeOddsButtonGroupViewKey()
  const specialBetValue = getSpecialBetValue(market, oddsBlockType)
  const disabledHome = isSelectionDisabled(enabledHomeFromViewController, homeRate)
  const disabledAway = isSelectionDisabled(enabledAwayFromViewController, awayRate)
  const homeTeamTxt = convertRate(authStore.isSignedIn, homeOutcome.rate)
  const awayTeamTxt = convertRate(authStore.isSignedIn, awayOutcome.rate)

  let homeOutcomeKey = homeKey
  let awayOutcomeKey = awayKey
  if (isLsportsEnv()) {
    homeOutcomeKey = homeOutcome.type
    awayOutcomeKey = awayOutcome.type
  }

  const marketType = odds?.type ?? ''
  const specifiers = JSON.stringify(market.specifiers)
  const homeSelectionValue = makeSelectionValue({
    marketType,
    specifiers,
    outcomeType: homeOutcomeKey,
    rate: homeRate,
    outcomeMetaId: homeOutcome.metaId,
    key: homeOutcomeKey,
    producerId: matchupStore.producerId,
  })
  const awaySelectionValue = makeSelectionValue({
    marketType,
    specifiers,
    outcomeType: awayOutcomeKey,
    rate: awayRate,
    outcomeMetaId: awayOutcome.metaId,
    key: awayOutcomeKey,
    producerId: matchupStore.producerId,
  })
  return (
    <Fragment>
      <div
        key={oddsButtonGroupViewKey}
        className="odds-block-list--team-btn"
      >
        <Radio.Group
          key={`${oddsButtonGroupViewKey}:home`}
          value={field}
          disabled={disabled}
          className="odds-block-radio-group"
        >
          <Radio.Button
            tabIndex={tabIndex}
            value={homeSelectionValue}
            onClick={onClickField}
            disabled={disabledHome}
            className={`btn-left odds-block-radio-btn ${homeRateChangeClass}`}
          >
            {!disabledHome && homeRateDiff !== 0 && (
              <ChangeNum className="left rate-change">
                <span className={`${homeRateChangeClass}-num`}>{Math.abs(homeRateDiff)}</span>
                {homeRateDiff > 0 ? PointUp : PointDown}
              </ChangeNum>
            )}

            <div className="odds-block-radio-btn--team">
              {oddsBlockType === BLOCK_TYPE.UNDEROVER ? (
                <span className="odds-block-radio-btn--team--type">언더</span>
              ) : (
                <span className="odds-block-radio-btn--team--type">홈 승</span>
              )}
              <span className="odds-block-radio-btn--team--num">
                  <span className="rate">{homeTeamTxt}</span>
                {oddsBlockType === BLOCK_TYPE.UNDEROVER && Down}
                </span>
            </div>
          </Radio.Button>

          <CenterWrap className="bottom-wrap">
            {oddsBlockType === BLOCK_TYPE.HANDICAP ? (
              <span className="point-type">H</span>
            ) : (
              <span className="point-type">U/O</span>
            )}
            <span className="point-num">{specialBetValue}</span>
            <span className="empty-area"/>
          </CenterWrap>

          <Radio.Button
            tabIndex={tabIndex}
            key={`${oddsButtonGroupViewKey}:away`}
            value={awaySelectionValue}
            onClick={onClickField}
            disabled={disabledAway}
            className={`odds-block-radio-btn ${awayRateChangeClass}`}
          >

            {!disabledAway && awayChangedRate !== 0 && (
              <ChangeNum className="rate-change">
                <span className={`${awayRateChangeClass}-num`}>{Math.abs(awayChangedRate)}</span>
                {awayChangedRate > 0 ? PointUp : PointDown}
              </ChangeNum>
            )}

            <div className="odds-block-radio-btn--team">
                <span className="odds-block-radio-btn--team--num">
                  <span className="rate">{awayTeamTxt}</span>
                  {oddsBlockType === BLOCK_TYPE.UNDEROVER && Up}
                </span>
              {oddsBlockType === BLOCK_TYPE.UNDEROVER ? (
                <span className="odds-block-radio-btn--team--type">오버</span>
              ) : (
                <span className="odds-block-radio-btn--team--type">원정 승</span>
              )}
            </div>
          </Radio.Button>
        </Radio.Group>
      </div>
    </Fragment>
  )
}

export default observer(OddsButtonGroup)
